import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { DECKS } from '../data/stimuli';
import { addCardToDeck, uploadDeckCardImage } from '../services/firestoreService';
import { LogoLockup } from '../components/LogoMark';
import '../styles/session.css';
import '../styles/deck-builder.css';
import '../styles/addCard.css';

const ACP_STEPS = [
  { num: 1, label: 'Name' },
  { num: 2, label: 'Photo' },
  { num: 3, label: 'Save' },
];

const HINT_TEXT = {
  1: 'Give your card a name — this is what gets shown during therapy sessions.',
  2: 'Search the web and select multiple photos, or use Camera / Photos.',
  3: "Looks good? Save it and it'll be added to your deck permanently.",
};

const PEXELS_KEY = process.env.REACT_APP_PEXELS_API_KEY;

export function AddCardPage() {
  const { deckName } = useParams();
  const navigate     = useNavigate();
  const { currentUser } = useAuth();
  const deck = DECKS.find(d => d.name === decodeURIComponent(deckName));
  const deckExamples = deck?.items?.slice(0, 3).map(i => i.label).join(', ') || 'Apple, Ball, Chair';

  // ── Wizard state ──────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);
  const [cardName, setCardName] = useState('');

  // Selected photos — first is main, rest are variations
  // Each: { url, isBlob, file? }
  const [selectedPhotos, setSelectedPhotos] = useState([]);

  // Web image search
  const [showWebSearch, setShowWebSearch] = useState(false);
  const [webQuery,      setWebQuery]      = useState('');
  const [webResults,    setWebResults]    = useState([]);
  const [webSearching,  setWebSearching]  = useState(false);
  const [webError,      setWebError]      = useState('');
  const [webPage,       setWebPage]       = useState(1);
  const [webHasMore,    setWebHasMore]    = useState(false);

  // Camera
  const [cameraOpen,   setCameraOpen]   = useState(false);
  const [cameraError,  setCameraError]  = useState('');
  const [cameraFacing, setCameraFacing] = useState('environment');

  // Save
  const [saving,         setSaving]         = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error,          setError]          = useState('');

  // ── Refs ──────────────────────────────────────────────────────────────────
  const photoInputRef = useRef(null);
  const videoRef      = useRef(null);
  const canvasRef     = useRef(null);
  const streamRef     = useRef(null);
  const webInputRef   = useRef(null);
  const mountedRef    = useRef(true);
  // Holds the real (upload + addCardToDeck) promise across the 10s UI
  // timeout in handleSave — if the user retries while it's still pending,
  // we re-attach to this instead of firing a second write, so a slow
  // request that eventually succeeds can't create a duplicate card.
  const saveInFlightRef = useRef(null);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      stopStream();
    };
  }, []);

  // Pre-fill web search with card name when panel opens
  useEffect(() => {
    if (showWebSearch && cardName.trim() && !webQuery) {
      setWebQuery(cardName.trim());
    }
  }, [showWebSearch]); // eslint-disable-line

  // ── Pexels search ─────────────────────────────────────────────────────────
  const doSearch = async (query, page = 1) => {
    if (!query.trim()) return;
    setWebSearching(true);
    setWebError('');
    try {
      const res = await fetch(
        `https://api.pexels.com/v1/search?query=${encodeURIComponent(query.trim())}&per_page=20&page=${page}&orientation=square`,
        { headers: { Authorization: PEXELS_KEY } }
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      const photos = (data.photos || []).map(p => ({
        id:  p.id,
        url: p.src.medium,
        alt: p.alt || query,
      }));
      setWebResults(page === 1 ? photos : prev => [...prev, ...photos]);
      setWebHasMore(!!data.next_page);
      setWebPage(page);
    } catch {
      setWebError('fetch_failed');
    } finally {
      setWebSearching(false);
    }
  };

  const handleWebSearch   = () => { setWebResults([]); setWebPage(1); doSearch(webQuery, 1); };
  const handleWebLoadMore = () => doSearch(webQuery, webPage + 1);

  const toggleWebPhoto = (photo) => {
    setSelectedPhotos(prev => {
      const exists = prev.findIndex(p => p.url === photo.url);
      if (exists !== -1) {
        return prev.filter((_, i) => i !== exists);
      }
      return [...prev, { url: photo.url, isBlob: false }];
    });
  };

  const openWebSearch = () => {
    setShowWebSearch(s => !s);
    setWebResults([]);
    setWebError('');
    if (!showWebSearch) {
      setTimeout(() => webInputRef.current?.focus(), 60);
    }
  };

  // ── Camera ────────────────────────────────────────────────────────────────
  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const openCamera = async (facingMode = 'environment') => {
    setCameraError('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera not supported in this browser.');
      return;
    }
    try {
      stopStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facingMode } }, audio: false,
      });
      if (!mountedRef.current) {
        // Component unmounted while the permission prompt was pending —
        // the effect cleanup already ran and can't stop a stream that
        // didn't exist yet, so stop it here instead of leaking it.
        stream.getTracks().forEach(t => t.stop());
        return;
      }
      streamRef.current = stream;
      setCameraFacing(facingMode);
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); }
      }, 50);
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraError('Camera permission denied. Please allow camera access and try again.');
      } else if (err.name === 'NotFoundError') {
        setCameraError('No camera found on this device.');
      } else {
        setCameraError('Could not open camera. Try using Photos instead.');
      }
    }
  };

  const flipCamera = () => openCamera(cameraFacing === 'environment' ? 'user' : 'environment');

  const capturePhoto = () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(blob => {
      if (!blob) return;
      const file    = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const url     = URL.createObjectURL(blob);
      setSelectedPhotos(prev => [...prev, { url, isBlob: true, file }]);
      closeCamera();
    }, 'image/jpeg', 0.92);
  };

  const closeCamera = () => { stopStream(); setCameraOpen(false); setCameraError(''); };

  // ── File pick ─────────────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const url = URL.createObjectURL(file);
    setSelectedPhotos(prev => [...prev, { url, isBlob: true, file }]);
    setShowWebSearch(false);
  };

  const removePhoto = (idx) => {
    setSelectedPhotos(prev => {
      const next = [...prev];
      if (next[idx]?.isBlob) URL.revokeObjectURL(next[idx].url);
      next.splice(idx, 1);
      return next;
    });
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!cardName.trim()) { setError('Please enter a card name.'); return; }
    if (!currentUser) { setError('Not signed in. Please refresh and try again.'); return; }
    setSaving(true);
    setError('');

    try {
      if (!saveInFlightRef.current) {
        saveInFlightRef.current = (async () => {
          const uploadOne = async (photo, progressCb) => {
            if (!photo.isBlob) return photo.url;
            const up = await uploadDeckCardImage(
              currentUser.uid, deckName, `card_${Date.now()}`, photo.file, progressCb
            );
            if (!up.success) throw new Error(up.error || 'Upload failed');
            return up.url;
          };

          const urls = [];
          for (let i = 0; i < selectedPhotos.length; i++) {
            const url = await uploadOne(selectedPhotos[i], i === 0 ? setUploadProgress : null);
            urls.push(url);
          }

          return addCardToDeck(currentUser.uid, deckName, {
            label:      cardName.trim(),
            image:      urls[0] || null,
            emoji:      deck?.emoji || '🃏',
            variations: urls.slice(1),
          });
        })().finally(() => { saveInFlightRef.current = null; });
      }

      const result = await Promise.race([
        saveInFlightRef.current,
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Save timed out. Check your connection and try again.')), 10000)
        ),
      ]);

      setSaving(false);
      if (result.success) {
        navigate(`/session/${encodeURIComponent(deckName)}`, {
          state: { autoSelect: cardName.trim() },
        });
      } else {
        setError(result.error || 'Failed to save card. Please try again.');
      }
    } catch (err) {
      setSaving(false);
      setError(err.message || 'An unexpected error occurred. Please try again.');
    }
  };

  const canNext = step === 1 ? cardName.trim().length > 0 : true;
  const mainPhoto = selectedPhotos[0];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="wz-page">

      {/* ── Camera modal ── */}
      {cameraOpen && (
        <div className="acp-camera-overlay">
          <div className="acp-camera-modal">
            <div className="acp-camera-header">
              <button className="acp-camera-close" type="button" onClick={closeCamera}>✕ Cancel</button>
              <span className="acp-camera-title">Take Photo</span>
              <button className="acp-camera-flip" type="button" onClick={flipCamera}>Flip</button>
            </div>
            <div className="acp-camera-viewport">
              <video ref={videoRef} className="acp-camera-video" autoPlay playsInline muted />
            </div>
            <div className="acp-camera-footer">
              <button className="acp-shutter-btn" type="button" onClick={capturePhoto}>
                <span className="acp-shutter-inner" />
              </button>
            </div>
          </div>
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      )}

      {/* ── Header ── */}
      <div className="session-header-bar">
        <button className="sc-back-link" type="button" onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/library')}>← Back</button>
        <div className="sc-logo-crop"><LogoLockup size={24} /></div>
        <div style={{ width: 60 }} />
      </div>

      <div className="wz-layout">
        {/* ── Stepper ── */}
        <div className="wz-stepper">
          {ACP_STEPS.map(({ num, label }) => (
            <div key={num} className="wz-step-item">
              <div className="wz-step-track">
                <div className={`wz-step-circle ${step === num ? 'wz-active' : step > num ? 'wz-done' : 'wz-pending'}`}>
                  {step > num ? '✓' : num}
                </div>
                {num < 3 && <div className={`wz-step-line ${step > num ? 'wz-line-done' : ''}`} />}
              </div>
              <span className={`wz-step-label ${step === num ? 'wz-label-active' : ''}`}>{label}</span>
            </div>
          ))}
        </div>

        {/* ── Main card ── */}
        <div className="wz-main">
          <div className="wz-card">

            {/* ══ Step 1: Name ══ */}
            {step === 1 && (
              <>
                <h2 className="wz-step-title">Step 1: Card Name</h2>
                <div className="wz-field">
                  <label className="wz-field-label">What is this card called?</label>
                  <input
                    className="db-input db-input-large"
                    type="text"
                    placeholder={`e.g. ${deckExamples}…`}
                    value={cardName}
                    autoFocus
                    onChange={e => { setCardName(e.target.value); setError(''); }}
                    onKeyDown={e => e.key === 'Enter' && cardName.trim() && setStep(2)}
                  />
                  <p className="wz-field-hint" style={{ marginTop: 8 }}>
                    Use the exact word you say during sessions — "ball", "cup", "chair", etc.
                  </p>
                </div>
              </>
            )}

            {/* ══ Step 2: Photo ══ */}
            {step === 2 && (
              <>
                <h2 className="wz-step-title">Step 2: Add Photos</h2>

                {/* Selected photos strip */}
                {selectedPhotos.length > 0 && (
                  <div className="acp-selected-strip">
                    {selectedPhotos.map((p, i) => (
                      <div key={p.url} className="acp-selected-thumb">
                        <img src={p.url} alt={`photo ${i + 1}`} className="acp-selected-thumb-img" />
                        <div className="acp-selected-badge">{i === 0 ? 'Main' : i + 1}</div>
                        <button className="acp-selected-remove" type="button" onClick={() => removePhoto(i)}>✕</button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="wz-field">
                  <label className="wz-field-label">
                    {selectedPhotos.length === 0 ? 'Choose a source' : 'Add more photos'}
                  </label>
                  <div className="db-img-source-row">
                    <button className="db-img-source-btn acp-cam-btn" type="button" onClick={() => openCamera()}>
                      📷 Camera
                    </button>
                    <button className="db-img-source-btn" type="button" onClick={() => photoInputRef.current?.click()}>
                      🖼 Photos
                    </button>
                    <button
                      className={`db-img-source-btn${showWebSearch ? ' active' : ''}`}
                      type="button"
                      onClick={openWebSearch}
                    >
                      🌐 Search Web
                    </button>
                  </div>
                  {cameraError && <p className="acp-camera-error">{cameraError}</p>}
                  {selectedPhotos.length > 0 && (
                    <p className="wz-field-hint" style={{ marginTop: 6 }}>
                      {selectedPhotos.length} photo{selectedPhotos.length !== 1 ? 's' : ''} selected — first is main, rest rotate as variations.
                    </p>
                  )}
                </div>

                {/* ── Web search panel ── */}
                {showWebSearch && (
                  <div className="acp-library-panel">
                    <div className="acp-web-search-row">
                      <input
                        ref={webInputRef}
                        className="acp-lib-search"
                        type="text"
                        placeholder={`Search photos — try "${cardName || 'apple'}"…`}
                        value={webQuery}
                        onChange={e => setWebQuery(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleWebSearch()}
                      />
                      <button
                        className="acp-web-search-btn"
                        type="button"
                        onClick={handleWebSearch}
                        disabled={!webQuery.trim() || webSearching}
                      >
                        {webSearching ? '…' : 'Go'}
                      </button>
                    </div>

                    {webError === 'fetch_failed' && (
                      <div className="acp-search-empty">
                        <p>Search failed. Check your connection and try again.</p>
                      </div>
                    )}

                    {!webError && webResults.length === 0 && !webSearching && (
                      <div className="acp-web-hint">
                        <p>Type a word and tap <strong>Go</strong> to find photos.</p>
                        <p>Tap multiple photos to select them all.</p>
                      </div>
                    )}

                    {webResults.length > 0 && (
                      <>
                        <div className="acp-lib-count">
                          {webResults.length} results · {selectedPhotos.filter(p => !p.isBlob).length} selected
                        </div>
                        <div className="acp-search-results">
                          {webResults.map(photo => {
                            const selIdx = selectedPhotos.findIndex(p => p.url === photo.url);
                            const isSelected = selIdx !== -1;
                            return (
                              <div
                                key={photo.id}
                                className={`acp-search-tile${isSelected ? ' selected' : ''}`}
                                onClick={() => toggleWebPhoto(photo)}
                              >
                                <img
                                  src={photo.url}
                                  alt={photo.alt}
                                  className="acp-search-tile-img"
                                  loading="lazy"
                                  onError={e => { e.currentTarget.parentElement.style.display = 'none'; }}
                                />
                                {isSelected && (
                                  <div className="acp-tile-sel-badge">
                                    {selIdx === 0 ? '★' : selIdx + 1}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {webHasMore && (
                          <button
                            className="acp-web-more-btn"
                            type="button"
                            onClick={handleWebLoadMore}
                            disabled={webSearching}
                          >
                            {webSearching ? 'Loading…' : 'Load more'}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}

                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
              </>
            )}

            {/* ══ Step 3: Review ══ */}
            {step === 3 && (
              <>
                <h2 className="wz-step-title">Step 3: Review &amp; Save</h2>
                <div className="acp-wz-review">
                  <div className="acp-wz-review-face" style={{ backgroundColor: deck?.color || '#f5f5f5' }}>
                    {mainPhoto
                      ? <img src={mainPhoto.url} alt={cardName} className="acp-wz-review-img" />
                      : <span className="acp-wz-review-emoji">{deck?.emoji || '🃏'}</span>
                    }
                  </div>
                  <p className="acp-wz-review-name">{cardName}</p>
                  <p className="acp-wz-review-deck">{deck?.emoji} {deckName}</p>
                </div>

                {selectedPhotos.length > 1 && (
                  <div className="acp-review-vars">
                    <p className="acp-review-vars-label">+ {selectedPhotos.length - 1} variation photo{selectedPhotos.length > 2 ? 's' : ''}</p>
                    <div className="acp-review-vars-row">
                      {selectedPhotos.slice(1).map((p, i) => (
                        <img key={i} src={p.url} alt={`var ${i + 1}`} className="acp-review-var-thumb" />
                      ))}
                    </div>
                  </div>
                )}

                {!mainPhoto && (
                  <p className="wz-field-hint" style={{ textAlign: 'center', marginTop: 8 }}>
                    No photo — the deck emoji will be shown during sessions.
                  </p>
                )}

                {error && (
                  <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', marginTop: 12 }}>
                    <p style={{ color: '#DC2626', fontSize: 13, margin: 0 }}>{error}</p>
                  </div>
                )}

                {uploadProgress !== null && uploadProgress < 100 && (
                  <div className="acp-progress-bar" style={{ marginTop: 12 }}>
                    <div className="acp-progress-fill" style={{ width: `${uploadProgress}%` }} />
                  </div>
                )}
              </>
            )}

          </div>
        </div>
      </div>

      {/* ── Fixed footer ── */}
      <div className="wz-footer">
        <div className="wz-footer-hint">
          <span className="wz-hint-text">{HINT_TEXT[step]}</span>
        </div>
        <div className="wz-footer-nav">
          {step > 1 && (
            <button className="wz-back-btn" type="button" onClick={() => setStep(s => s - 1)} disabled={saving}>
              ← Back
            </button>
          )}
          {step < 3 ? (
            <button className="wz-next-btn" type="button" disabled={!canNext} onClick={() => setStep(s => s + 1)}>
              Next →
            </button>
          ) : (
            <button className="wz-next-btn" type="button" disabled={saving || !cardName.trim()} onClick={handleSave}>
              {saving ? 'Saving…' : 'Save Card'}
            </button>
          )}
        </div>
      </div>

    </div>
  );
}
